// Shared reusable horizontal carousel controller for homepage sections that
// declare [data-carousel] - currently sections/product-carousel.liquid AND
// sections/exclusive-bundles.liquid. One controller powers both instead of
// duplicating this logic per section.
//
// MOBILE-ONLY (below 768px). At `md` and up, both sections revert to their
// original CSS grid (no scroll, no snap, no arrows - see the `md:` classes
// in each section's Liquid) - see isMobileViewport() below. Every listener
// body re-checks isMobileViewport() so the controller is inert at desktop
// even though listeners stay bound (see the resize handler for why we
// don't bother unbinding: a CSS grid has no scrollLeft effect, so there is
// nothing to visually clean up when crossing into desktop - only `current`
// needs to be reset so a later return to mobile starts sane).
//
// [data-carousel-track] must be the SAME element that has overflow-x-auto
// (i.e. the actual native scroll container) - not a wrapper around it -
// otherwise scrollLeft/scrollBy/the 'scroll' listener silently do nothing.
//
// RTL-safe by construction: every position/distance calculation below uses
// Element.getBoundingClientRect() (always physical/viewport pixels,
// independent of dir) instead of offsetLeft/scrollLeft absolute values,
// whose zero-point and sign convention differ across browsers under
// `dir="rtl"`. Movement is done with track.scrollBy({ left: delta }) using
// a PHYSICAL delta between two getBoundingClientRect()s - increasing
// scrollLeft always moves the viewport physically rightward and decreasing
// it always moves the viewport physically leftward in every current
// evergreen browser's standardized RTL scrollLeft convention, in both
// dir="ltr" and dir="rtl" - so no RTL branching is needed for movement
// either. Chevron direction is flipped purely via CSS (`rtl:rotate-180` in
// the Liquid markup) - the index/geometry math never changes.
//
// Navigation LOOPS (circular): Next past the last slide wraps to the first,
// Previous before the first wraps to the last, via plain index modulo -
// `((index % slides.length) + slides.length) % slides.length` - no arrow
// is ever disabled while 2+ slides exist (Liquid only renders the arrows
// at all when there are 2+ slides, so a single-slide carousel already has
// no arrows in the DOM - nothing further to disable here).
//
// Seamless infinite loop via cloned edge slides: a clone of the LAST real
// slide is inserted before slide 0, and a clone of the FIRST real slide is
// appended after the last slide (both built with cloneNode(true) at init
// time - see initCarousel()). A wrap (goTo() called with index < 0 or
// index >= slides.length, from an arrow click, 'step'-mode autoplay, or
// the last->first/first->last case of a manual swipe) now animates
// SMOOTHLY onto the adjacent clone exactly like an ordinary single-card
// move, because the clone is pixel-identical to the real card it stands in
// for - there is no visual cut, jump, flash, or reverse motion, in either
// dir="ltr" or dir="rtl". The instant that clone becomes the nearest
// slide - whether because goTo()'s smooth scroll just settled, or because
// native touch/trackpad swiping carried the user past a real edge on its
// own - the real slide underneath is silently swapped in via an
// unanimated `track.scrollLeft` adjustment (see syncToNearest()). Since
// the clone and the real slide it stands in for render identical pixels at
// that exact scroll offset, this swap is invisible; `current`/the exposed
// slide index NEVER refers to a clone, only ever to one of the N real
// slides. Clones are marked `inert` + `aria-hidden="true"` (never
// focusable, clickable, or announced) with every `id`/
// `data-shopify-editor-block` attribute stripped from themselves and all
// descendants (so getElementById-style lookups and Theme Editor
// block-selection can never land on a clone instead of the real card) -
// their <form>s/quantity inputs/Pack-of-2 controls exist only as inert
// pixels, so duplicating that DOM carries none of the double-submit/
// desynced-state risk a genuinely interactive clone would.
//
// Every root is scoped to itself (no shared/global state), so multiple
// carousel sections on one page are safe, and the init guard prevents
// double-binding on Shopify Theme Editor section reloads. Optional
// per-root autoplay is described further down.
//
// Arrow vertical position ([data-carousel-controls]) tracks the FULL
// active CARD, not its image - card height is not constant (Pack of 2
// controls, promo text, quantity stepper, translated string length all
// change it), so the anchor is measured live via
// slides[current].getBoundingClientRect() against the [data-carousel]
// root's own rect, then written into a --carousel-arrow-y CSS custom
// property that the Liquid markup consumes via
// `top-[var(--carousel-arrow-y)] -translate-y-1/2`. Re-measured after
// every navigation settle, every native swipe/scroll settle, every
// resize, and via a per-active-slide ResizeObserver (feature-detected)
// so a height change on the CURRENTLY ACTIVE card alone re-centers the
// arrows without a page reload - see updateArrowPosition()/
// observeActiveSlide() below.
//
// Autoplay is entirely config-driven per root via data attributes read in
// initCarousel() - no per-section logic lives here, so this stays ONE
// controller instead of two:
//   data-carousel-mode="continuous" data-carousel-speed="48"  (Products)
//     - a slow, continuous requestAnimationFrame scroll (~48 css px/sec by
//       default). Mandatory scroll-snap fights sub-pixel continuous
//       movement, so it is disabled on the track (scrollSnapType='none')
//       only while this is actually running, and restored - settling
//       smoothly to the nearest full card - the moment autoplay pauses.
//       Direction is derived once per run by comparing two real
//       getBoundingClientRect()s (never hardcoded), so it is correct in
//       both LTR and RTL for free.
//   data-carousel-mode="step" data-carousel-interval="3000"  (Bundles)
//     - a discrete timer that calls the exact same goTo(current + 1) a
//       Next click makes, once every N ms. Normal scroll-snap and settle
//       behavior are untouched.
// Autoplay starts as soon as the root initializes and is on-screen -
// seeded synchronously from the root's own getBoundingClientRect(), never
// waiting on a scroll/resize event or the IntersectionObserver's own first
// callback (see isRootVisible()). Both modes then PAUSE (never permanently
// stop) while off-screen (IntersectionObserver), document.hidden is true,
// the viewport is at the desktop grid breakpoint, prefers-reduced-motion is
// set, or a real interaction happens on the track - an arrow click, a
// horizontal wheel/trackpad tick, or a touch/pointer drag that RESOLVES as
// horizontal intent (abs(dx) > 10 && abs(dx) > abs(dy) * 1.2, tracked from
// each gesture's own start point via beginGestureTracking()/
// trackGestureMove()) - and auto-RESUME INACTIVITY_RESUME_DELAY (2500ms)
// after the interaction ends, as long as continuous horizontal movement (an
// active drag) hasn't kept resetting that countdown (see
// pauseForInteraction()/scheduleResume()). A touch/pointer gesture that
// never resolves as horizontal - i.e. a normal vertical page-scroll
// starting on the track, or a mere touchstart/pointerdown/hover with no
// confirmed horizontal movement - never pauses autoplay, not even
// temporarily, and none of these listeners ever call preventDefault, so
// native page scrolling is never blocked or delayed. Autoplay's own
// programmatic scrolling is never treated as an interaction because the
// pause listeners are bound to input events (touchstart/touchmove/
// pointerdown/pointermove/wheel/click), never to the track's own 'scroll'
// event. Everything autoplay-related
// is cleaned up (rAF/timer/inactivity-timer cancelled, both observers
// disconnected, the document-level visibility listener removed) on
// 'shopify:section:unload' via root._veloCarousel.destroy() - see the
// bottom of this file.
//
// Global singleton guard: both product-carousel.liquid AND
// exclusive-bundles.liquid each render their own
// <script src="{{ 'carousel.js' | asset_url }}"> tag (classic, non-module
// script), and a classic <script src> RE-EXECUTES its top-level code once
// per <script> tag that references it, even when both tags point at the
// same URL/cached file. The per-root `dataset.carouselInitialized` guard
// inside initCarousel() alone stops a second execution from re-attaching
// per-root scroll/resize/pointer listeners (it returns before any listener
// is added), but it does NOT stop a second execution's own top-level
// document.addEventListener('DOMContentLoaded'/'shopify:section:load', ...)
// registrations from existing as a second, separate listener. Guarding
// window.Velo.__carouselLoaded here makes every execution after the first
// a complete no-op - only the first <script> tag's execution ever runs.
(function () {
  window.Velo = window.Velo || {};
  if (window.Velo.__carouselLoaded) return;
  window.Velo.__carouselLoaded = true;

  var DRAG_THRESHOLD = 6; // px of mouse movement before a click is treated as a drag, not a tap
  var SETTLE_DELAY = 420; // ms - long enough for the smooth scroll to finish before re-reading geometry
  var MOBILE_QUERY = '(max-width: 767px)'; // matches the `md:` (768px) breakpoint used in the Liquid markup

  function isMobileViewport() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initCarousel(root) {
    if (root.dataset.carouselInitialized === 'true') return;

    var track = root.querySelector('[data-carousel-track]');
    var prevBtn = root.querySelector('[data-carousel-prev]');
    var nextBtn = root.querySelector('[data-carousel-next]');
    var controls = root.querySelector('[data-carousel-controls]');
    if (!track) return;

    var slides = Array.prototype.slice.call(track.querySelectorAll(':scope > [data-carousel-item]:not([data-carousel-clone])'));
    if (slides.length < 2) return;

    root.dataset.carouselInitialized = 'true';

    // Cloned edge slides for a seamless infinite loop - see the file header
    // comment. `slides` above is queried BEFORE insertion and excludes
    // `[data-carousel-clone]` defensively, so `current`/all index math only
    // ever refers to one of the N real slides - clones exist purely as a
    // transient visual bridge for wraps, never as an exposed index.
    function sanitizeClone(clone) {
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[id]').forEach(function (el) { el.removeAttribute('id'); });
      clone.querySelectorAll('[data-shopify-editor-block]').forEach(function (el) { el.removeAttribute('data-shopify-editor-block'); });
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-carousel-clone', 'true');
      clone.setAttribute('inert', '');
      clone.inert = true;
    }

    var cloneLast = slides[slides.length - 1].cloneNode(true);
    var cloneFirst = slides[0].cloneNode(true);
    sanitizeClone(cloneLast);
    sanitizeClone(cloneFirst);
    track.insertBefore(cloneLast, slides[0]);
    track.appendChild(cloneFirst);

    // Clones only ever exist for the mobile scroll track - at `md:` and up
    // both sections revert to a CSS grid/`contents` layout where an extra
    // DOM child would otherwise render as an unwanted extra card, so an
    // inline `display: none` (always wins over any `md:` utility class,
    // regardless of Tailwind's generated CSS source order) hides both
    // clones whenever the viewport isn't mobile-width. Kept in sync on
    // every mobile/desktop resize crossing - see handleResize() below.
    function updateCloneVisibility() {
      var display = isMobileViewport() ? '' : 'none';
      cloneLast.style.display = display;
      cloneFirst.style.display = display;
    }
    updateCloneVisibility();

    // Aligns scrollLeft onto real slide 0 - inserting cloneLast as track's
    // first child grows the scrollable content to the left of slide 0
    // (whenever clones are visible), which would otherwise show cloneLast
    // instead of slide 0 at the default scrollLeft=0. Harmless/no-op
    // whenever clones are display:none (they contribute zero width then).
    // Runs synchronously before first paint, same physical-rect delta
    // technique used throughout this file.
    function alignToSlideZero() {
      var trackRect = track.getBoundingClientRect();
      var slide0Rect = slides[0].getBoundingClientRect();
      track.scrollLeft += (slide0Rect.left - trackRect.left);
    }
    alignToSlideZero();

    // Per-root autoplay config - see the file header comment for the two
    // supported modes. An unrecognized/absent mode simply never autoplays;
    // swipe/arrows are completely unaffected either way.
    var autoplayMode = root.dataset.carouselMode || 'none';
    var autoplaySpeed = parseFloat(root.dataset.carouselSpeed) || 32; // css px/sec, 'continuous' only
    var autoplayInterval = parseInt(root.dataset.carouselInterval, 10) || 3000; // ms, 'step' only

    var current = 0;
    var syncingFromScroll = false;

    // Vertically anchors [data-carousel-controls] to the currently active
    // slide's FULL rendered height (never the image alone), relative to
    // the [data-carousel] root - the same element the controls overlay is
    // absolutely positioned against, so writing a plain px offset here
    // positions the overlay correctly with no further math needed.
    function updateArrowPosition() {
      if (!controls || !isMobileViewport()) return;
      var activeSlide = slides[current];
      if (!activeSlide) return;
      var rootRect = root.getBoundingClientRect();
      var slideRect = activeSlide.getBoundingClientRect();
      var centerY = slideRect.top - rootRect.top + slideRect.height / 2;
      controls.style.setProperty('--carousel-arrow-y', centerY + 'px');
    }

    // Re-subscribes the ResizeObserver (if supported) to whichever slide
    // is now active, so a height change on THAT card (Pack of 2, promo
    // text, quantity, price, translated wrapping) re-centers the arrows
    // without needing a scroll/resize event to also fire. One observer
    // per root, only ever watching a single element at a time.
    var resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(function () { updateArrowPosition(); })
      : null;
    var observedSlide = null;
    function observeActiveSlide() {
      if (!resizeObserver) return;
      var activeSlide = slides[current];
      if (activeSlide === observedSlide) return;
      if (observedSlide) resizeObserver.unobserve(observedSlide);
      observedSlide = activeSlide;
      if (observedSlide) resizeObserver.observe(observedSlide);
    }

    // Which slot (real slide OR clone) is nearest the track's own left
    // edge right now, by physical distance - used to keep `current` in
    // sync with native touch/trackpad/drag scrolling, and to recover from
    // any staleness right before a button click. Includes both clones so a
    // swipe that carries the user past a real edge on its own (no goTo()
    // call at all) is detected too.
    function computeNearestSlot() {
      var trackRect = track.getBoundingClientRect();
      var candidates = [{ el: cloneLast, index: slides.length - 1, isClone: true }];
      slides.forEach(function (slide, i) { candidates.push({ el: slide, index: i, isClone: false }); });
      candidates.push({ el: cloneFirst, index: 0, isClone: true });
      var nearest = candidates[0];
      var closestDist = Infinity;
      candidates.forEach(function (c) {
        var dist = Math.abs(c.el.getBoundingClientRect().left - trackRect.left);
        if (dist < closestDist) {
          closestDist = dist;
          nearest = c;
        }
      });
      return nearest;
    }

    // Resolves the nearest slot AND, if it happens to be a clone, silently
    // swaps the real slide underneath in via an unanimated scrollLeft
    // adjustment (see the file header comment) - always returns a real
    // slide index, never a clone. This is the single place the seamless
    // loop's illusion is completed; called from every place that used to
    // call the old computeNearestIndex().
    function syncToNearest() {
      var slot = computeNearestSlot();
      if (slot.isClone) {
        var realRect = slides[slot.index].getBoundingClientRect();
        var cloneRect = slot.el.getBoundingClientRect();
        track.scrollLeft += (realRect.left - cloneRect.left);
      }
      return slot.index;
    }

    // index may be < 0 or >= slides.length on purpose (current -1 / +1 at
    // the ends) - that is exactly what signals a wrap. A wrap animates
    // smoothly onto the adjacent clone (bridgeSlide) instead of the real
    // target slide, then silently normalizes onto the real slide once the
    // smooth scroll settles - see the file header comment.
    function goTo(index) {
      var bridgeSlide = index < 0 ? cloneLast : (index >= slides.length ? cloneFirst : null);
      current = ((index % slides.length) + slides.length) % slides.length;
      var targetSlide = bridgeSlide || slides[current];
      var trackRect = track.getBoundingClientRect();
      var targetRect = targetSlide.getBoundingClientRect();
      var delta = targetRect.left - trackRect.left;
      syncingFromScroll = true;
      observeActiveSlide();
      track.scrollBy({ left: delta, behavior: 'smooth' });
      window.setTimeout(function () {
        if (bridgeSlide) {
          var realRect = slides[current].getBoundingClientRect();
          var cloneRect = bridgeSlide.getBoundingClientRect();
          track.scrollLeft += (realRect.left - cloneRect.left);
        }
        syncingFromScroll = false;
        updateArrowPosition();
      }, SETTLE_DELAY);
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        if (!isMobileViewport()) return;
        pauseForInteraction();
        current = syncToNearest();
        goTo(current - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (!isMobileViewport()) return;
        pauseForInteraction();
        current = syncToNearest();
        goTo(current + 1);
      });
    }

    // Keeps `current` in sync with any scroll source (native touch swipe,
    // the mouse-drag below, trackpad) - not just arrow clicks - so the next
    // arrow click (and its wrap math) continues from the correct card.
    var scrollDebounce;
    track.addEventListener('scroll', function () {
      if (!isMobileViewport() || syncingFromScroll) return;
      window.clearTimeout(scrollDebounce);
      scrollDebounce = window.setTimeout(function () {
        current = syncToNearest();
        observeActiveSlide();
        updateArrowPosition();
      }, 120);
    }, { passive: true });

    // Recompute on viewport resize/orientation change, and handle crossing
    // the mobile/desktop breakpoint explicitly:
    // - desktop -> mobile: re-derive `current` from whatever is actually
    //   scrolled into view right now, so the first arrow click after
    //   re-entering mobile moves from a valid position (not a stale index
    //   left over from before the resize).
    // - mobile -> desktop: nothing to visually clean up - the section is
    //   now `md:grid`, which ignores the track's scrollLeft entirely - but
    //   `current` is reset to 0 defensively so a later resize back to
    //   mobile (without a full page reload, e.g. rotating a foldable/
    //   resizable window) doesn't start mid-list for no reason.
    var wasMobile = isMobileViewport();
    var resizeDebounce;
    function handleResize() {
      window.clearTimeout(resizeDebounce);
      resizeDebounce = window.setTimeout(function () {
        var nowMobile = isMobileViewport();
        updateCloneVisibility();
        if (nowMobile && !wasMobile) {
          // Clones just became visible again (0 -> normal width), which
          // shifts what scrollLeft=0 points at - realign onto real slide 0
          // rather than trusting syncToNearest() here, since the nearest
          // slot right after this shift may resolve to cloneLast (which
          // normalizes onto the LAST real slide, not slide 0).
          alignToSlideZero();
          current = 0;
          observeActiveSlide();
          if (!interactionPausing && isIntersecting) startAutoplay();
        } else if (!nowMobile && wasMobile) {
          current = 0;
          pauseAutoplay();
        }
        wasMobile = nowMobile;
        if (nowMobile) updateArrowPosition();
      }, 150);
    }
    window.addEventListener('resize', handleResize);

    // Mouse/stylus click-and-drag scrolling for the mobile-width carousel
    // (e.g. a touch laptop or foldable in a narrow/portrait width using a
    // pointer instead of a finger). Touch pointers are ignored here since
    // touch already scrolls the track natively via overflow-x-auto. Inert
    // at `md:` and up since the section is a non-scrolling grid there.
    var pointerId = null;
    var dragStartX = 0;
    var dragScrollStart = 0;
    var dragMoved = false;
    var dragging = false;

    track.style.cursor = 'grab';

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || !isMobileViewport()) return;
      dragging = true;
      dragMoved = false;
      dragStartX = e.clientX;
      dragScrollStart = track.scrollLeft;
      pointerId = e.pointerId;
      track.style.cursor = 'grabbing';
      if (track.setPointerCapture) {
        try { track.setPointerCapture(pointerId); } catch (err) { /* noop */ }
      }
    });

    track.addEventListener('pointermove', function (e) {
      if (!dragging || e.pointerId !== pointerId) return;
      var dx = e.clientX - dragStartX;
      if (Math.abs(dx) > DRAG_THRESHOLD) dragMoved = true;
      // Relative to the drag's own starting scrollLeft (read back from the
      // browser, not recomputed), so this is correct under any RTL
      // scrollLeft convention without branching.
      track.scrollLeft = dragScrollStart - dx;
    });

    function endDrag(e) {
      if (!dragging || (e && e.pointerId !== pointerId)) return;
      dragging = false;
      pointerId = null;
      track.style.cursor = 'grab';
      current = syncToNearest();
      observeActiveSlide();
      updateArrowPosition();
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // Suppress the click a real mouse-drag over a card control would
    // otherwise fire (e.g. a variant option label, Add to Cart, or the
    // product/bundle link).
    track.addEventListener('click', function (e) {
      if (dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        dragMoved = false;
      }
    }, true);

    // Prevent the browser's native image-drag ghost from fighting our drag
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    // --- Autoplay -----------------------------------------------------
    //
    // Autoplay is temporarily PAUSED (never permanently stopped) by any
    // real interaction on the track - touch/pointer down, mouse drag,
    // horizontal wheel/trackpad, or an arrow click - and automatically
    // RESUMES INACTIVITY_RESUME_DELAY ms after the interaction ends, as
    // long as continuous movement hasn't kept resetting that countdown
    // (see scheduleResume()). This is bound to input events only
    // (touchstart/touchmove/pointerdown/pointermove/wheel/click), never to
    // 'scroll', so autoplay's own programmatic scrolling can never pause
    // itself. A purely vertical page-scroll gesture starting on the track
    // is allowed to pause autoplay too (simpler + still auto-resumes), but
    // preventDefault is never called by any of these listeners, so native
    // vertical page scrolling is never blocked.
    var INACTIVITY_RESUME_DELAY = 2500; // ms of no further interaction before autoplay resumes
    var interactionPausing = false; // true while temporarily paused because of a user interaction
    var inactivityTimer = null;
    var autoplayRAF = null; // 'continuous' mode
    var autoplayDirectionSign = null;
    var lastFrameTime = null;
    var stepTimer = null; // 'step' mode
    var snapDisabled = false;
    var isIntersecting = false;

    function disableSnap() {
      if (snapDisabled) return;
      snapDisabled = true;
      track.style.scrollSnapType = 'none';
    }

    function restoreSnap() {
      if (!snapDisabled) return;
      snapDisabled = false;
      track.style.scrollSnapType = '';
    }

    // The root's own current on-screen state, computed synchronously from
    // its live geometry - used only to start autoplay immediately on init
    // without waiting for the IntersectionObserver's own (asynchronous,
    // sometimes delayed) first callback. The IntersectionObserver below
    // remains the source of truth for later scroll-away/return.
    function isRootVisible() {
      var rect = root.getBoundingClientRect();
      var vw = window.innerWidth || document.documentElement.clientWidth;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      return rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;
    }

    function canRunAutoplay() {
      return autoplayMode !== 'none'
        && !interactionPausing
        && !prefersReducedMotion()
        && !document.hidden
        && isIntersecting
        && isMobileViewport();
    }

    // Direction is derived once per run from two real rects - never
    // hardcoded - so 'forward' is correct in both LTR and RTL for free,
    // matching the rest of this file's RTL-safety approach.
    function computeDirectionSign() {
      var r0 = slides[0].getBoundingClientRect();
      var r1 = slides[1].getBoundingClientRect();
      return r1.left >= r0.left ? 1 : -1;
    }

    function stepContinuousFrame(timestamp) {
      if (!canRunAutoplay() || autoplayMode !== 'continuous') {
        autoplayRAF = null;
        lastFrameTime = null;
        return;
      }
      if (lastFrameTime == null) lastFrameTime = timestamp;
      var dt = (timestamp - lastFrameTime) / 1000;
      lastFrameTime = timestamp;
      if (autoplayDirectionSign == null) autoplayDirectionSign = computeDirectionSign();

      track.scrollLeft += autoplayDirectionSign * autoplaySpeed * dt;

      // The raw scrollLeft increment above naturally carries continuous
      // autoplay onto the clone past the last real slide (pixel-identical
      // to it, so nothing visibly changes) - syncToNearest() then silently
      // swaps the real slide underneath the instant that clone becomes
      // nearest, completing the seamless loop with no hard-coded jump.
      var nearest = syncToNearest();
      if (nearest !== current) {
        current = nearest;
        observeActiveSlide();
        updateArrowPosition();
      }
      autoplayRAF = window.requestAnimationFrame(stepContinuousFrame);
    }

    function startContinuous() {
      if (autoplayRAF != null || !canRunAutoplay() || autoplayMode !== 'continuous') return;
      disableSnap();
      lastFrameTime = null;
      autoplayDirectionSign = null;
      autoplayRAF = window.requestAnimationFrame(stepContinuousFrame);
    }

    function pauseContinuous() {
      if (autoplayRAF == null) return;
      window.cancelAnimationFrame(autoplayRAF);
      autoplayRAF = null;
      lastFrameTime = null;
    }

    function scheduleStep() {
      window.clearTimeout(stepTimer);
      stepTimer = window.setTimeout(function () {
        stepTimer = null;
        if (!canRunAutoplay() || autoplayMode !== 'step') return;
        goTo(current + 1);
        scheduleStep();
      }, autoplayInterval);
    }

    function startStep() {
      if (stepTimer != null || !canRunAutoplay() || autoplayMode !== 'step') return;
      scheduleStep();
    }

    function pauseStep() {
      if (stepTimer == null) return;
      window.clearTimeout(stepTimer);
      stepTimer = null;
    }

    function startAutoplay() {
      if (autoplayMode === 'continuous') startContinuous();
      else if (autoplayMode === 'step') startStep();
    }

    function pauseAutoplay() {
      pauseContinuous();
      pauseStep();
    }

    // Settles the track to the nearest full card and restores mandatory
    // scroll-snap whenever continuous autoplay is interrupted (snap and a
    // sub-pixel continuous scroll fight each other, so snap stays off only
    // while continuous autoplay is actually running - see disableSnap()).
    // 'step' mode never disables snap, so this is a no-op for Bundles.
    function settleForPause() {
      if (autoplayMode === 'continuous' && snapDisabled) {
        restoreSnap();
        current = syncToNearest();
        goTo(current);
      }
    }

    // Pauses autoplay for a real interaction and (re)starts the
    // INACTIVITY_RESUME_DELAY countdown to auto-resume. Safe to call
    // repeatedly (e.g. once per wheel tick) - the pause/settle side effect
    // only runs once per pause, but the countdown is refreshed every time.
    function pauseForInteraction() {
      var wasPausing = interactionPausing;
      interactionPausing = true;
      if (!wasPausing) {
        pauseAutoplay();
        settleForPause();
      }
      scheduleResume();
    }

    // Resets the auto-resume countdown - but only while already paused for
    // an interaction, so an idle mouse hover (pointermove with no button
    // held, no prior pointerdown) can never pause/extend anything on its
    // own. Called continuously during an active touch/pointer drag so the
    // countdown only starts once real movement actually stops.
    function scheduleResume() {
      if (!interactionPausing) return;
      window.clearTimeout(inactivityTimer);
      inactivityTimer = window.setTimeout(function () {
        inactivityTimer = null;
        interactionPausing = false;
        if (canRunAutoplay()) startAutoplay();
      }, INACTIVITY_RESUME_DELAY);
    }

    // A touch/pointer gesture only pauses autoplay once it resolves as
    // HORIZONTAL intent - a purely vertical page-scroll gesture starting on
    // the track must never pause anything, not even temporarily. Tracked
    // from each gesture's own start point (never the previous move point),
    // per the same threshold Next/Previous-adjacent interactions use
    // elsewhere in this file: abs(dx) > 10 && abs(dx) > abs(dy) * 1.2. One
    // shared set of tracking vars is safe because the touch-* and (non-
    // touch) pointer-* listeners below are mutually exclusive for a single
    // physical gesture.
    var gestureStartX = null;
    var gestureStartY = null;
    var gestureHorizontalConfirmed = false;

    function beginGestureTracking(x, y) {
      gestureStartX = x;
      gestureStartY = y;
      gestureHorizontalConfirmed = false;
    }

    function trackGestureMove(x, y) {
      if (gestureStartX === null) return;
      if (gestureHorizontalConfirmed) {
        pauseForInteraction(); // already resolved horizontal - keep resetting the inactivity timer
        return;
      }
      var dx = x - gestureStartX;
      var dy = y - gestureStartY;
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        gestureHorizontalConfirmed = true;
        pauseForInteraction();
      }
    }

    function endGestureTracking() {
      if (gestureHorizontalConfirmed) scheduleResume();
      gestureStartX = null;
      gestureStartY = null;
      gestureHorizontalConfirmed = false;
    }

    track.addEventListener('touchstart', function (e) {
      var t = e.touches[0];
      if (t) beginGestureTracking(t.clientX, t.clientY);
    }, { passive: true });
    track.addEventListener('touchmove', function (e) {
      var t = e.touches[0];
      if (t) trackGestureMove(t.clientX, t.clientY);
    }, { passive: true });
    track.addEventListener('touchend', endGestureTracking, { passive: true });
    track.addEventListener('touchcancel', endGestureTracking, { passive: true });

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return; // handled by touchstart above
      beginGestureTracking(e.clientX, e.clientY);
    });
    track.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch') return;
      trackGestureMove(e.clientX, e.clientY);
    });
    track.addEventListener('pointerup', function (e) {
      if (e.pointerType === 'touch') return;
      endGestureTracking();
    });
    track.addEventListener('pointercancel', function (e) {
      if (e.pointerType === 'touch') return;
      endGestureTracking();
    });

    // A wheel/trackpad tick is already a single discrete event - its own
    // deltaX/deltaY comparison IS its horizontal-intent resolution, no
    // separate gesture-start tracking is needed the way touch/pointer drags
    // (which arrive as a start + many move events) require.
    track.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) pauseForInteraction();
    }, { passive: true });

    // Pause while the section is off-screen, resume automatically when it
    // comes back into view (unless still paused for an interaction). One
    // observer per root.
    var autoplayIO = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.target !== root) return;
            isIntersecting = entry.isIntersecting;
            if (isIntersecting) {
              if (!interactionPausing) startAutoplay();
            } else {
              pauseAutoplay();
            }
          });
        }, { threshold: 0.25 })
      : null;
    if (autoplayIO) {
      autoplayIO.observe(root);
    }

    // Autoplay must begin as soon as the carousel initializes and is
    // visible - it must NOT wait for a scroll/resize event or for the
    // IntersectionObserver's own first callback to land. isIntersecting is
    // seeded synchronously from the root's current geometry and autoplay
    // is started immediately if applicable; the IntersectionObserver above
    // keeps it in sync for every later scroll-away/return.
    isIntersecting = isRootVisible();
    if (isIntersecting && !interactionPausing) startAutoplay();

    function handleVisibilityChange() {
      if (document.hidden) {
        pauseAutoplay();
      } else if (!interactionPausing && isIntersecting) {
        startAutoplay();
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Full teardown on Shopify section reload/removal - called by the
    // 'shopify:section:unload' listener at the bottom of this file.
    root._veloCarousel = {
      destroy: function () {
        pauseAutoplay();
        restoreSnap();
        window.clearTimeout(inactivityTimer);
        if (autoplayIO) autoplayIO.disconnect();
        if (resizeObserver) resizeObserver.disconnect();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('resize', handleResize);
        cloneLast.remove();
        cloneFirst.remove();
        root.dataset.carouselInitialized = 'false';
      }
    };

    // Position the arrows correctly on first render, not only after the
    // first user interaction. Autoplay itself starts from the
    // IntersectionObserver's own initial callback, not from here, so it
    // only ever starts once the section is actually visible.
    observeActiveSlide();
    updateArrowPosition();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-carousel]').forEach(initCarousel);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });
  if (document.readyState !== 'loading') {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-carousel]').forEach(function (root) {
      if (root._veloCarousel) root._veloCarousel.destroy();
    });
  });
})();
